import { matchPath } from "react-router-dom";

/**
 * USAGE-ANALYTICS-1 / FA-2 — the routes a usage beacon may name.
 *
 * ⚑ GENERATED from `usage_events.CLIENT_ROUTE_TEMPLATES`, never typed. There are 77, and the
 * server does EXACT set membership on whatever the client sends:
 *
 *     "The declared template, or the sentinel. NEVER the string the client sent. A query string, a
 *      fragment, or a path with an identifier in it is not in the declared list, so it lands on
 *      the sentinel without any parsing of the thing we do not trust."
 *
 * So sending `location.pathname` would be WORSE than sending nothing: every page with an id in its
 * path would bucket as `(unresolved)` for a page we do know. The client resolves to a template
 * first, and omits `route` entirely when nothing matches — omitting is honest, and an unresolvable
 * path is noise that looks like data.
 *
 * ⚑ A guard in `usageRoutes.test.js` reads the Python and fails if these two lists diverge. FA-2's
 * fence runs both ways: a frontend route missing here corrupts the data, and one here the frontend
 * has dropped makes this list a record of what used to exist. Regenerate rather than edit —
 * `gen_usage_routes.py` in the lane scratchpad is the source.
 *
 * Ordered longest-first so a concrete path wins over a parameterised sibling: `/my-workspace/goals`
 * must not resolve to `/my-workspace/:section`.
 */
export const USAGE_ROUTE_TEMPLATES = Object.freeze([
  "/admin/teachers/:teacherId/lessons/:videoId",
  "/admin/teachers/:teacherId/growth",
  "/admin/teachers/:teacherId/lessons",
  "/admin/teachers/:teacherId",
  "/master-admin/organizations/:organizationId",
  "/master-admin/users/:userId",
  "/master-admin/videos/:videoId",
  "/master-admin/workspaces/:ownerUserId",
  "/teachers/:teacherId/action-plan",
  "/teachers/:teacherId/coaching",
  "/teachers/:teacherId/history",
  "/teachers/:teacherId/latest-lesson",
  "/teachers/:teacherId/operations",
  "/teachers/:teacherId/reflections",
  "/admin/access",
  "/admin/growth",
  "/admin/library",
  // W39 (C-362 §3) — the other half of FA-2, which I missed on the first pass: the declaration is
  // TWO lists, this one and the server's, and this file's own test asserts they are equal exactly.
  // My own W39 guard checked only the backend side, so it passed while the invariant was broken.
  "/admin/my-leadership",
  "/admin/progress",
  "/admin/reports",
  "/admin/schedule",
  "/admin/settings",
  "/admin/setup",
  "/admin/team",
  "/master-admin/ai-quality",
  "/master-admin/audit",
  "/master-admin/auth-activity",
  "/master-admin/dependencies",
  "/master-admin/incidents",
  "/master-admin/organizations",
  "/master-admin/storage",
  "/master-admin/support",
  "/master-admin/usage",
  "/master-admin/users",
  "/master-admin/videos",
  "/master-admin/workspaces",
  "/my-workspace/goals",
  "/my-workspace/reflections",
  "/observation/new",
  "/settings/notifications",
  "/teacher/lessons",
  "/teacher/profile",
  "/my-workspace/:section",
  "/teachers/:teacherId",
  "/videos/:videoId",
  "/",
  "/admin",
  "/all-star-library",
  "/calendar",
  "/coaching",
  "/cohorts",
  "/consent",
  "/dashboard",
  "/design-preview",
  "/frameworks",
  "/getting-started",
  "/lessons",
  "/login",
  "/master-admin",
  "/master-schedule",
  "/my-badges",
  "/my-coaching",
  "/my-growth",
  "/my-lessons",
  "/my-plans",
  "/my-profile",
  "/my-workspace",
  "/notifications",
  "/onboarding",
  "/privacy",
  "/privacy-review",
  "/recognition",
  "/recognition-review",
  "/record",
  "/reports",
  "/school-setup",
  "/signup",
  "/teachers",
  "/teachers-room",
  "/videos",
]);

/**
 * The declared template this pathname belongs to, or null when none does.
 *
 * null means "send no route at all" rather than "send the sentinel": the server decides what an
 * unresolved event looks like, and a client that names the sentinel itself has started deciding.
 */
export function resolveUsageRoute(pathname) {
  if (typeof pathname !== "string" || !pathname) return null;
  // Strip any query or fragment before matching: both are things the server would refuse anyway.
  const path = pathname.split("?")[0].split("#")[0];
  for (const template of USAGE_ROUTE_TEMPLATES) {
    if (matchPath({ path: template, end: true }, path)) return template;
  }
  return null;
}

export default resolveUsageRoute;
